import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Modal } from '../../elements/Modal';
import OneMonthImage from '../../../assets/product/30days.png';
import ThreeMonthImage from '../../../assets/product/oneMonthNew.png';
import SixMonthImage from '../../../assets/product/threeMonthsNew.png';
import ProductImage1 from '../../../assets/product/product1.webp';
import ProductImage3 from '../../../assets/product/product3.webp';
import Omega3Image from '../../../assets/omega3/1.webp';
import {
  useChangePlanMutation,
  useGetPlanChangeOptionsQuery,
} from '../../../features/customerApiSlice';
import { getBrowserLocale } from '../../../utils/language';

/**
 * Format an ISO date string for the user's locale.
 */
function formatDate(isoString, locale = 'en-GB') {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function isPastOrNow(isoString) {
  if (!isoString) return false;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

function resolvePlanImage({ productType, cadenceMonths }) {
  if (String(productType).toLowerCase() === 'omega-3') {
    return { primary: Omega3Image, secondary: null };
  }
  if (Number(cadenceMonths) === 3) {
    return { primary: SixMonthImage, secondary: ProductImage3 };
  }
  return { primary: ThreeMonthImage || OneMonthImage, secondary: ProductImage1 };
}

function formatEuro(value) {
  const amount = Number(value || 0);
  return `€${amount.toFixed(2)}`;
}

function cadenceMonthsFromCurrent(current) {
  const explicitCadence = Number(current?.cadenceMonths);
  if (Number.isFinite(explicitCadence) && explicitCadence > 0) {
    return explicitCadence === 3 ? 3 : 1;
  }

  const productType = String(current?.product_type || '').toLowerCase();
  const durationNum = parseInt(String(current?.duration ?? ''), 10);

  if (productType === 'omega-3') {
    return durationNum === 3 ? 3 : 1;
  }

  // Riise legacy mapping: 3 => monthly cadence, 6 => every 3 months cadence.
  return durationNum === 6 ? 3 : 1;
}

/**
 * Modal that drives the change-plan flow. Two stages:
 *   1) "options" – pick a target plan from the server-computed list.
 *   2) "confirm" – restate the change in plain language; confirm.
 *
 * On confirm the mutation is fired. If the response comes back as `mode: 'redirect'`
 * (rare — only when the silent server-side create fails) we redirect to Stripe
 * Checkout transparently. Otherwise we close the modal and let the parent refetch.
 */
function ChangePlanModal({
  open,
  onClose,
  userSubscriptionId,
  selectedLang,
  onSuccess,
}) {
  const { t } = useTranslation();
  const locale = getBrowserLocale(selectedLang);

  const [stage, setStage] = useState('options');
  const [selectedOption, setSelectedOption] = useState(null);

  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useGetPlanChangeOptionsQuery(
    { userSubscriptionId },
    { skip: !open || !userSubscriptionId },
  );

  const [changePlanMutation, { isLoading: isChanging }] = useChangePlanMutation();

  // Reset internal state every time the modal is reopened so old selections
  // never leak into a fresh flow.
  useEffect(() => {
    if (open) {
      setStage('options');
      setSelectedOption(null);
    }
  }, [open]);

  // Refetch when the modal opens to make sure dates/prices are fresh.
  useEffect(() => {
    if (open && userSubscriptionId) {
      refetch();
    }
  }, [open, userSubscriptionId, refetch]);

  const current = data?.current;
  const options = data?.options || [];

  /** Billing cadence in months (1 = monthly, 3 = every 3 months). Server sends `cadenceMonths` on every option. */
  const cadenceMonthsFromOption = (option) => {
    if (option?.cadenceMonths != null && !Number.isNaN(Number(option.cadenceMonths))) {
      return Number(option.cadenceMonths);
    }
    if (option?.duration === 6) return 3;
    return 1;
  };

  const optionTitle = (option) =>
    cadenceMonthsFromOption(option) === 3
      ? t('CustomerSubs_ChangePlan_OptionTitle_3m')
      : t('CustomerSubs_ChangePlan_OptionTitle_1m');

  const currentPlanTitle = useMemo(() => {
    if (!current) return '';
    const currentCadence = cadenceMonthsFromCurrent(current);
    return currentCadence === 3
      ? t('CustomerSubs_ChangePlan_OptionTitle_3m')
      : t('CustomerSubs_ChangePlan_OptionTitle_1m');
  }, [current, t]);

  const optionPriceLabel = (option) => {
    const cadence = cadenceMonthsFromOption(option);
    const baseAmount = Number(option?.amount || 0);
    const addonAmount = Number(option?.addon?.newAmount || 0);
    const totalAmount = baseAmount + (option?.addon?.newAmount != null ? addonAmount : 0);
    const totalPerMonth = cadence > 0 ? Number((totalAmount / cadence).toFixed(2)) : totalAmount;
    if (cadence === 3 || option.duration === 6) {
      return t('CustomerSubs_ChangePlan_OptionPrice_3m', {
        amount: totalAmount.toFixed(2),
        perMonth: totalPerMonth.toFixed(2),
      });
    }
    return t('CustomerSubs_ChangePlan_OptionPrice_1m', {
      amount: totalAmount.toFixed(2),
    });
  };

  const optionCurrentPrice = (option) => {
    const baseAmount = Number(current?.amount || 0);
    const addonAmount = Number(option?.addon?.currentAmount || 0);
    const totalAmount = baseAmount + (option?.addon?.currentAmount != null ? addonAmount : 0);
    return formatEuro(totalAmount);
  };

  const savingsPercentForOption = (option) => {
    if (!current || !option) return null;
    const optionCadenceMonths = cadenceMonthsFromOption(option);
    const currentBase = Number(current?.amount || 0);
    const currentAddon = Number(option?.addon?.currentAmount || 0);
    const nextBase = Number(option?.amount || 0);
    const nextAddon = Number(option?.addon?.newAmount || 0);
    const hasAddon = option?.addon?.newAmount != null;

    const currentCycleTotal = hasAddon ? currentBase + currentAddon : currentBase;
    const nextCycleTotal = hasAddon ? nextBase + nextAddon : nextBase;
    const currentPerMonth =
      currentCadenceMonths > 0 ? currentCycleTotal / currentCadenceMonths : currentCycleTotal;
    const nextPerMonth = optionCadenceMonths > 0 ? nextCycleTotal / optionCadenceMonths : nextCycleTotal;
    if (!Number.isFinite(currentPerMonth) || currentPerMonth <= 0 || !Number.isFinite(nextPerMonth)) return null;
    const savingsPct = ((currentPerMonth - nextPerMonth) / currentPerMonth) * 100;
    if (!Number.isFinite(savingsPct) || savingsPct <= 0) return null;
    return Math.round(savingsPct);
  };

  const currentCadenceMonths = cadenceMonthsFromCurrent(current);
  const selectedCadenceMonths = selectedOption ? cadenceMonthsFromOption(selectedOption) : 1;

  const pricingSummary = useMemo(() => {
    if (!current || !selectedOption) return null;
    const currentBase = Number(current.amount || 0);
    const currentAddon = Number(selectedOption?.addon?.currentAmount || 0);
    const nextBase = Number(selectedOption.amount || 0);
    const nextAddon = Number(selectedOption?.addon?.newAmount || 0);

    const hasAddon = selectedOption?.addon?.newAmount != null;
    const currentCycleTotal = hasAddon ? currentBase + currentAddon : currentBase;
    const nextCycleTotal = hasAddon ? nextBase + nextAddon : nextBase;

    const currentPerMonth = currentCadenceMonths > 0 ? currentCycleTotal / currentCadenceMonths : currentCycleTotal;
    const nextPerMonth =
      selectedCadenceMonths > 0 ? nextCycleTotal / selectedCadenceMonths : nextCycleTotal;

    const savingsPct =
      currentPerMonth > 0 ? ((currentPerMonth - nextPerMonth) / currentPerMonth) * 100 : 0;

    return {
      hasAddon,
      currentCycleTotal,
      nextCycleTotal,
      currentPerMonth,
      nextPerMonth,
      savingsPct: Number.isFinite(savingsPct) ? savingsPct : 0,
    };
  }, [current, selectedOption, currentCadenceMonths, selectedCadenceMonths]);

  const currentHeadlineAmount = useMemo(() => {
    const base = Number(current?.amount || 0);
    const addon = Number(selectedOption?.addon?.currentAmount || 0);
    return base + (selectedOption?.addon?.currentAmount != null ? addon : 0);
  }, [current, selectedOption]);

  const selectedHeadlineAmount = useMemo(() => {
    const base = Number(selectedOption?.amount || 0);
    const addon = Number(selectedOption?.addon?.newAmount || 0);
    return base + (selectedOption?.addon?.newAmount != null ? addon : 0);
  }, [selectedOption]);

  const savingsBadgeLabel =
    pricingSummary && pricingSummary.savingsPct > 0
      ? t('CustomerSubs_ChangePlan_SaveBadge', { percent: Math.round(pricingSummary.savingsPct) })
      : null;

  const currentComparisonPoints = useMemo(() => {
    if (!current) return [];
    const points = [
      t('CustomerSubs_ChangePlan_Compare_BilledEvery', {
        amount: formatEuro(current.amount),
        cadence: currentCadenceMonths,
      }),
      t('CustomerSubs_ChangePlan_Compare_EquivalentPerMonth', {
        amount: formatEuro(Number(current.amount || 0) / currentCadenceMonths),
      }),
      currentCadenceMonths === 3
        ? t('CustomerSubs_ChangePlan_Compare_LessFrequent')
        : t('CustomerSubs_ChangePlan_Compare_MoreFlexible'),
    ];
    if (current?.product_type === 'riise_formula' && selectedOption?.addon?.newAmount != null) {
      const addonCurrentAmount = Number(selectedOption?.addon?.currentAmount || 0);
      points.push(
        `Omega-3 add-on active, ${t('CustomerSubs_ChangePlan_Compare_BilledEvery', {
          amount: formatEuro(addonCurrentAmount),
          cadence: currentCadenceMonths,
        }).toLowerCase()}`,
      );
    }
    return points;
  }, [current, currentCadenceMonths, selectedOption, t]);

  const nextComparisonPoints = useMemo(() => {
    if (!selectedOption) return [];
    const points = [
      t('CustomerSubs_ChangePlan_Compare_BilledEvery', {
        amount: formatEuro(selectedOption.amount),
        cadence: selectedCadenceMonths,
      }),
      t('CustomerSubs_ChangePlan_Compare_EquivalentPerMonth', {
        amount: formatEuro(Number(selectedOption.amount || 0) / selectedCadenceMonths),
      }),
      selectedCadenceMonths === 3
        ? t('CustomerSubs_ChangePlan_Compare_FewerRenewals')
        : t('CustomerSubs_ChangePlan_Compare_ShorterCycle'),
    ];
    if (selectedOption?.addon?.newAmount != null) {
      points.push(
        t('CustomerSubs_ChangePlan_Compare_AddonUpdates', {
          amount: formatEuro(selectedOption.addon.newAmount),
          cadence: selectedOption.addon.cadenceMonths ?? selectedCadenceMonths,
        }),
      );
    }
    return points;
  }, [selectedOption, selectedCadenceMonths, t]);

  const unitLabel = (months) =>
    t('CustomerSubs_ChangePlan_Unit_Month', { count: months });

  const effectiveCopy = (option) => {
    if (!current || !option) return '';
    const cadence = cadenceMonthsFromOption(option);
    const startsImmediately = isPastOrNow(option.effectiveDate);
    if (startsImmediately) {
      return t('CustomerSubs_ChangePlan_Effective_Immediate_Recurring', {
        amount: option.amount?.toFixed(2),
        duration: cadence,
        unit: unitLabel(cadence),
      });
    }
    return t('CustomerSubs_ChangePlan_Effective_Recurring', {
      date: formatDate(option.effectiveDate, locale),
      amount: option.amount?.toFixed(2),
      duration: cadence,
      unit: unitLabel(cadence),
    });
  };

  const chargeCopy = (option) =>
    isPastOrNow(option?.effectiveDate)
      ? t('CustomerSubs_ChangePlan_StartsImmediately')
      : t('CustomerSubs_ChangePlan_NoChargeToday');

  const handlePick = (option) => {
    setSelectedOption(option);
    setStage('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedOption) return;
    try {
      const result = await changePlanMutation({
        userSubscriptionId,
        targetSubId: selectedOption.targetSubId,
      }).unwrap();

      if (result?.mode === 'redirect' && result?.checkoutUrl) {
        toast.info(t('CustomerSubs_ChangePlan_Redirecting'));
        window.location.href = result.checkoutUrl;
        return;
      }

      toast.success(t('CustomerSubs_ChangePlan_Success'));
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error(
        e?.data?.message || e?.message || t('CustomerSubs_ChangePlan_Error'),
      );
    }
  };

  const handleBack = () => {
    setStage('options');
  };

  return (
    <Modal
      open={open}
      handleClose={() => {
        if (isChanging) return;
        onClose?.();
      }}
      className="!t-max-w-[95vw] lg:!t-max-w-[520px] !t-rounded-lg !t-px-0"
      childrenClass="t-items-stretch"
      enableClickOutsideClose={false}
    >
      <div className="t-w-full t-px-6 t-pb-6 t-pt-2">
        {stage === 'options' && (
          <>
            <h2 className="t-text-xl t-font-bold t-m-0 t-mb-1">
              {t('CustomerSubs_ChangePlan_Modal_Title')}
            </h2>
            <p className="t-text-sm t-text-slate-600 t-m-0 t-mb-4">
              {t('CustomerSubs_ChangePlan_Modal_Subtitle')}
            </p>

            {isFetching && (
              <p className="t-text-sm t-text-slate-500 t-m-0">
                {t('CustomerSubs_Checking')}
              </p>
            )}

            {!isFetching && (isError || options.length === 0) && (
              <p className="t-text-sm t-text-slate-700 t-m-0">
                {t('CustomerSubs_ChangePlan_NoOptions')}
              </p>
            )}

            {!isFetching && options.length > 0 && (
              <div className="t-flex t-flex-col t-gap-3">
                {options.map((option) => {
                  const optionImage = resolvePlanImage({
                    productType: current?.product_type,
                    cadenceMonths: cadenceMonthsFromOption(option),
                  });
                  const optionSavings = savingsPercentForOption(option);
                  return (
                    <button
                      key={option.targetSubId}
                      type="button"
                      onClick={() => handlePick(option)}
                      className="t-relative t-text-left t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-slate-200 t-bg-white t-px-4 t-py-3 hover:t-border-secondary hover:t-shadow-sm t-transition-colors"
                    >
                      {optionSavings != null && (
                        <span className="t-absolute t-top-2 t-right-2 t-inline-flex t-items-center t-rounded-full t-bg-emerald-600 t-px-2.5 t-py-1 t-text-[11px] t-font-bold t-text-white">
                          {t('CustomerSubs_ChangePlan_SaveBadge', { percent: optionSavings })}
                        </span>
                      )}
                      <div className="t-flex t-items-center t-gap-3">
                        <div className="t-flex t-items-end">
                          <img
                            src={optionImage.primary}
                            alt={optionTitle(option)}
                            className="t-h-16 t-w-16 t-rounded-lg t-object-contain"
                          />
                          {optionImage.secondary && (
                            <img
                              src={optionImage.secondary}
                              alt={optionTitle(option)}
                              className="-t-ml-2 t-h-16 t-w-20 t-rounded-lg t-object-contain"
                            />
                          )}
                        </div>
                        {/* <span className="t-text-xs t-font-semibold t-text-slate-500">
                          {String(current?.product_type || '').toLowerCase() === 'omega-3'
                            ? t('CustomerSubs_ChangePlan_Product_Omega3')
                            : t('CustomerSubs_ChangePlan_Product_Riise')}
                        </span> */}
                      </div>
                    <div className="t-flex t-items-center t-justify-between t-gap-2">
                      <span className="t-text-base t-font-semibold t-text-slate-900">
                        {optionTitle(option)}
                      </span>
                      <div className="t-flex t-items-center t-gap-2">
                        <span className="t-text-xs lg:t-text-sm t-font-medium t-text-slate-400 t-line-through">
                          {optionCurrentPrice(option)}
                        </span>
                        <span className="t-text-sm lg:t-text-base t-font-semibold t-text-secondary">
                          {optionPriceLabel(option)}
                        </span>
                      </div>
                    </div>
                    <p className="t-m-0 t-text-sm t-text-slate-600">
                      {effectiveCopy(option)}
                    </p>
                    {option.addon?.newAmount != null && (
                      <p className="t-m-0 t-text-sm t-text-slate-700">
                        {t('CustomerSubs_ChangePlan_AddonAttached', {
                          amount: Number(option.addon.newAmount).toFixed(2),
                          cadence: option.addon.cadenceMonths ?? cadenceMonthsFromOption(option),
                        })}
                      </p>
                    )}
                    <p className="t-m-0 t-text-xs t-font-semibold t-text-emerald-700">
                      {chargeCopy(option)}
                    </p>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {stage === 'confirm' && selectedOption && (
          <>
            <h2 className="t-text-xl t-font-bold t-m-0 t-mb-1">
              {t('CustomerSubs_ChangePlan_Confirm_Title')}
            </h2>
            <div className="t-mb-3 t-grid t-grid-cols-1 sm:t-grid-cols-2 t-gap-3">
              <div className="t-rounded-lg t-border t-border-slate-200 t-bg-slate-50 t-p-3">
                <p className="t-m-0 t-mb-2 t-text-[11px] t-font-semibold t-uppercase t-text-slate-500">
                  {t('CustomerSubs_ChangePlan_Current')}
                </p>
                <div className="t-flex t-items-center t-gap-3">
                  {(() => {
                    const currentImage = resolvePlanImage({
                      productType: current?.product_type,
                      cadenceMonths: currentCadenceMonths,
                    });
                    return (
                      <div className="t-flex t-items-end">
                        <img
                          src={currentImage.primary}
                          alt={currentPlanTitle}
                          className="t-h-12 t-w-12 t-rounded-md t-object-contain t-bg-white"
                        />
                        {currentImage.secondary && (
                          <img
                            src={currentImage.secondary}
                            alt={currentPlanTitle}
                            className="-t-ml-1.5 t-h-12 t-w-14 t-rounded-md t-object-contain t-bg-white"
                          />
                        )}
                      </div>
                    );
                  })()}
                  <div>
                    <p className="t-m-0 t-text-sm t-font-semibold t-text-slate-900">{currentPlanTitle}</p>
                    <p className="t-m-0 t-text-xs t-text-slate-600">
                      €{currentHeadlineAmount.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="t-mt-3 t-space-y-1.5">
                  {currentComparisonPoints.map((point) => (
                    <p key={`current-${point}`} className="t-m-0 t-text-sm t-text-slate-700">
                      ✓ {point}
                    </p>
                  ))}
                </div>
              </div>
              <div className="t-relative t-rounded-lg t-border t-border-secondary/30 t-bg-teal-bg t-p-3">
                <p className="t-m-0 t-mb-2 t-text-[11px] t-font-semibold t-uppercase t-text-secondary">
                  {t('CustomerSubs_ChangePlan_NewPlan')}
                </p>
                <div className="t-flex t-items-center t-gap-3">
                  {(() => {
                    const selectedImage = resolvePlanImage({
                      productType: current?.product_type,
                      cadenceMonths: cadenceMonthsFromOption(selectedOption),
                    });
                    return (
                      <div className="t-flex t-items-end">
                        <img
                          src={selectedImage.primary}
                          alt={optionTitle(selectedOption)}
                          className="t-h-12 t-w-12 t-rounded-lg t-object-contain t-bg-white"
                        />
                        {selectedImage.secondary && (
                          <img
                            src={selectedImage.secondary}
                            alt={optionTitle(selectedOption)}
                            className="-t-ml-1.5 t-h-12 t-w-14 t-rounded-lg t-object-contain t-bg-white"
                          />
                        )}
                      </div>
                    );
                  })()}
                  <div>
                    <p className="t-m-0 t-text-sm t-font-semibold t-text-slate-900">
                      {optionTitle(selectedOption)}
                    </p>
                    <p className="t-m-0 t-text-xs t-text-slate-600">
                      €{selectedHeadlineAmount.toFixed(2)}
                    </p>
                  </div>
                  {savingsBadgeLabel && (
                    <span className="t-absolute t-top-2 t-right-2 t-inline-flex t-items-center t-rounded-full t-bg-emerald-600 t-px-2.5 t-py-1 t-text-[11px] t-font-bold t-text-white">
                      {savingsBadgeLabel}
                    </span>
                  )}
                </div>
                <div className="t-mt-3 t-space-y-1.5">
                  {nextComparisonPoints.map((point) => (
                    <p key={`next-${point}`} className="t-m-0 t-text-sm t-text-slate-700">
                      ✓ {point}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            {/* {selectedOption.addon?.newAmount != null && (
              <div className="t-mb-3">
                <p className="t-m-0 t-text-xs t-font-semibold t-text-secondary">
                  {t('CustomerSubs_ChangePlan_Omega3Summary_Title')}
                </p>
                <div className="t-mt-2 t-grid t-grid-cols-1 sm:t-grid-cols-2 t-gap-2.5">
                  <div className="t-rounded-lg t-border t-border-slate-200 t-bg-white t-p-2.5">
                    <p className="t-m-0 t-text-[11px] t-font-semibold t-uppercase t-text-slate-500">
                      {t('CustomerSubs_ChangePlan_Current')}
                    </p>
                    <p className="t-m-0 t-mt-1 t-text-sm t-font-semibold t-text-slate-900">
                      {formatEuro(selectedOption.addon.currentAmount)}
                    </p>
                    <p className="t-m-0 t-mt-0.5 t-text-xs t-text-slate-700">
                      {t('CustomerSubs_ChangePlan_Compare_BilledEvery', {
                        amount: formatEuro(selectedOption.addon.currentAmount),
                        cadence: currentCadenceMonths,
                      })}
                    </p>
                  </div>
                  <div className="t-rounded-lg t-border t-border-secondary/30 t-bg-teal-bg t-p-2.5">
                    <p className="t-m-0 t-text-[11px] t-font-semibold t-uppercase t-text-secondary">
                      {t('CustomerSubs_ChangePlan_NewPlan')}
                    </p>
                    <p className="t-m-0 t-mt-1 t-text-sm t-font-semibold t-text-slate-900">
                      {formatEuro(selectedOption.addon.newAmount)}
                    </p>
                    <p className="t-m-0 t-mt-0.5 t-text-xs t-text-slate-700">
                      {t('CustomerSubs_ChangePlan_Compare_BilledEvery', {
                        amount: formatEuro(selectedOption.addon.newAmount),
                        cadence: selectedOption.addon.cadenceMonths ?? selectedCadenceMonths,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )} */}
            <p className="t-m-0 t-mb-5 t-text-xs t-font-semibold t-text-emerald-700">
              {chargeCopy(selectedOption)}
            </p>

            <div className="t-flex t-flex-col sm:t-flex-row t-items-stretch sm:t-items-center t-gap-3">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isChanging}
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60"
              >
                {isChanging
                  ? t('CustomerSubs_ChangePlan_Loading')
                  : t('CustomerSubs_ChangePlan_Confirm_Yes')}
              </button>
              <button
                type="button"
                onClick={handleBack}
                disabled={isChanging}
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-slate-100 t-text-slate-700 hover:t-bg-slate-200 disabled:t-opacity-60"
              >
                {t('CustomerSubs_ChangePlan_Confirm_No')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ChangePlanModal;
